<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" @close="() => {}" class="relative z-50">

      <!-- Backdrop -->
      <TransitionChild
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <!-- Outer div owns the position transition, inner panel owns the wiggle — keeps them from fighting over transform -->
            <div :style="jumpStyle" class="transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
            <DialogPanel
                :class="['w-full max-w-lg rounded-xl bg-gray-900 border border-gray-700 shadow-2xl', { wiggle: isWiggling }]"
            >

              <!-- Header -->
              <div class="px-6 pt-5 pb-4 border-b border-gray-700">
                <DialogTitle class="text-base font-semibold text-white">Quick feedback</DialogTitle>
                <p class="mt-0.5 text-sm text-gray-400">Takes about 60 seconds — helps us a lot</p>
              </div>

              <!-- Questions -->
              <div class="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">

                <!-- Q1: Overall satisfaction (stars, required) -->
                <div>
                  <label class="block text-sm font-medium text-gray-200 mb-1">
                    How satisfied are you with the tool overall?
                    <span class="text-red-400 ml-0.5">*</span>
                  </label>
                  <p class="text-xs text-gray-500 mb-2">Required</p>
                  <div class="flex gap-1.5">
                    <button
                        v-for="star in 5"
                        :key="star"
                        type="button"
                        @click="form.overallRating = star"
                        @mouseenter="hoverOverall = star"
                        @mouseleave="hoverOverall = 0"
                        class="transition-transform duration-100 hover:scale-125 focus:outline-none"
                    >
                      <svg
                          class="w-8 h-8 transition-colors duration-100"
                          :class="star <= (hoverOverall || form.overallRating) ? 'text-yellow-400' : 'text-gray-600'"
                          fill="currentColor" viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                    <span v-if="form.overallRating" class="ml-2 self-center text-sm text-gray-400">
                      {{ overallLabel }}
                    </span>
                  </div>
                </div>

                <!-- Q2: Ease of use (1–5 scale, required) -->
                <div>
                  <label class="block text-sm font-medium text-gray-200 mb-1">
                    How easy is it to find and submit metrics for your agents?
                    <span class="text-red-400 ml-0.5">*</span>
                  </label>
                  <p class="text-xs text-gray-500 mb-2">Required</p>
                  <div class="w-fit">
                    <div class="flex gap-2">
                      <button
                          v-for="n in 5"
                          :key="n"
                          type="button"
                          @click="form.easeRating = n"
                          class="w-10 h-10 rounded-lg text-sm font-medium transition-colors focus:outline-none"
                          :class="form.easeRating === n
                            ? 'bg-blue-600 text-white border border-blue-500'
                            : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-500 hover:text-white'"
                      >
                        {{ n }}
                      </button>
                    </div>
                    <div class="flex justify-between mt-1">
                      <span class="text-xs text-gray-500">Very difficult</span>
                      <span class="text-xs text-gray-500">Very easy</span>
                    </div>
                  </div>
                </div>

                <!-- Q3: Pain point (multiple choice, required) -->
                <div>
                  <label class="block text-sm font-medium text-gray-200 mb-1">
                    What's the most frustrating part of using this tool?
                    <span class="text-red-400 ml-0.5">*</span>
                  </label>
                  <p class="text-xs text-gray-500 mb-2">Required</p>
                  <div class="grid grid-cols-2 gap-2">
                    <button
                        v-for="option in painPointOptions"
                        :key="option.value"
                        type="button"
                        @click="form.painPoint = option.value; if (option.value !== 'other') form.painPointDetail = ''"
                        class="px-3 py-2.5 rounded-lg text-sm text-left transition-colors focus:outline-none border"
                        :class="form.painPoint === option.value
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white'"
                    >
                      {{ option.label }}
                    </button>
                  </div>
                  <Transition
                      enter-active-class="transition-all duration-200 ease-out"
                      enter-from-class="opacity-0 -translate-y-1"
                      enter-to-class="opacity-100 translate-y-0"
                      leave-active-class="transition-all duration-150 ease-in"
                      leave-from-class="opacity-100 translate-y-0"
                      leave-to-class="opacity-0 -translate-y-1"
                  >
                    <div v-if="form.painPoint === 'other'" class="mt-2">
                      <textarea
                          v-model="form.painPointDetail"
                          rows="2"
                          placeholder="Tell us more..."
                          class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                      />
                    </div>
                  </Transition>
                </div>

                <!-- Q4: Missing feature (yes/no + conditional text) -->
                <div>
                  <label class="block text-sm font-medium text-gray-200 mb-1">
                    Is there a feature you wish this tool had?
                  </label>
                  <div class="flex gap-2 mb-2">
                    <button
                        type="button"
                        @click="form.missingFeature = true"
                        class="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none border"
                        :class="form.missingFeature === true
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'"
                    >
                      Yes
                    </button>
                    <button
                        type="button"
                        @click="form.missingFeature = false; form.missingFeatureDetail = ''"
                        class="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none border"
                        :class="form.missingFeature === false
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'"
                    >
                      No
                    </button>
                  </div>
                  <Transition
                      enter-active-class="transition-all duration-200 ease-out"
                      enter-from-class="opacity-0 -translate-y-1"
                      enter-to-class="opacity-100 translate-y-0"
                      leave-active-class="transition-all duration-150 ease-in"
                      leave-from-class="opacity-100 translate-y-0"
                      leave-to-class="opacity-0 -translate-y-1"
                  >
                    <div v-if="form.missingFeature === true">
                      <textarea
                          v-model="form.missingFeatureDetail"
                          rows="2"
                          placeholder="What would you add?"
                          class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                      />
                    </div>
                  </Transition>
                </div>

                <!-- Q5: Open feedback (optional) -->
                <div>
                  <label class="block text-sm font-medium text-gray-200 mb-1">
                    Anything else you'd like us to know?
                    <span class="text-gray-500 font-normal ml-1">(optional)</span>
                  </label>
                  <textarea
                      v-model="form.openFeedback"
                      rows="2"
                      placeholder="What's working well? What's frustrating?"
                      class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                  />
                </div>

                <!-- Error -->
                <p v-if="submitError" class="text-sm text-red-400">{{ submitError }}</p>

              </div>

              <!-- Footer -->
              <div class="flex items-center justify-between px-6 py-4 border-t border-gray-700">
                <div class="flex flex-col gap-1">
                  <p v-if="hasBounced" class="text-sm text-yellow-400">Ha! You can't escape that easily 😏</p>
                  <button
                      type="button"
                      @click="onSnoozeClick"
                      class="text-sm text-gray-500 hover:text-gray-300 transition-colors text-left"
                  >
                    Remind me later
                  </button>
                </div>
                <button
                    type="button"
                    @click="submit"
                    :disabled="!canSubmit || isSubmitting"
                    class="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {{ isSubmitting ? 'Sending…' : 'Submit' }}
                </button>
              </div>

            </DialogPanel>
            </div>
          </TransitionChild>
        </div>
      </div>

    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import api from '@/services/api';

const emit = defineEmits<{
  (e: 'submitted'): void;
}>();

const hoverOverall = ref(0);
const isWiggling = ref(false);
const hasBounced = ref(false);
const jumpStyle = ref<{ transform?: string }>({});

const onSnoozeClick = () => {
  hasBounced.value = true;
  const maxX = window.innerWidth  / 2 - 300;
  const maxY = Math.min(window.innerHeight * 0.15, 100);
  const x = (Math.random() * 2 - 1) * maxX;
  const y = (Math.random() * 2 - 1) * maxY;
  jumpStyle.value = { transform: `translate(${x}px, ${y}px)` };
  // Wait for the position transition to finish, then wiggle
  setTimeout(() => {
    isWiggling.value = true;
    setTimeout(() => isWiggling.value = false, 500);
  }, 520);
};

const form = reactive({
  overallRating: 0,
  easeRating: 0,
  painPoint: '' as string,
  painPointDetail: '',
  missingFeature: null as boolean | null,
  missingFeatureDetail: '',
  openFeedback: '',
});

const painPointOptions = [
  { value: 'finding_agent',   label: 'Finding the right agent' },
  { value: 'submitting_form', label: 'Submitting the metric form' },
  { value: 'metric_clarity',  label: 'Understanding what each metric means' },
  { value: 'nothing',         label: "Nothing, it's fine" },
  { value: 'other',           label: 'Something else' },
];

const overallLabel = computed(() => {
  const labels: Record<number, string> = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Great', 5: 'Excellent!' };
  return labels[form.overallRating] ?? '';
});

const canSubmit = computed(() =>
  form.overallRating > 0 &&
  form.easeRating > 0 &&
  form.painPoint !== ''
);

const isSubmitting = ref(false);
const submitError = ref('');

const submit = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  submitError.value = '';

  try {
    await api.post('/feedback', {
      overallRating: form.overallRating,
      easeRating: form.easeRating,
      painPoint: form.painPoint,
      painPointDetail: form.painPointDetail?.trim() || null,
      missingFeature: form.missingFeature,
      missingFeatureDetail: form.missingFeatureDetail?.trim() || null,
      openFeedback: form.openFeedback?.trim() || null,
    });
    emit('submitted');
  } catch (err) {
    console.error('Failed to submit feedback:', err);
    submitError.value = 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
@keyframes wiggle {
  0%, 100% { transform: translateX(0); }
  15%       { transform: translateX(-6px); }
  30%       { transform: translateX(6px); }
  45%       { transform: translateX(-5px); }
  60%       { transform: translateX(5px); }
  75%       { transform: translateX(-3px); }
  90%       { transform: translateX(3px); }
}

.wiggle {
  animation: wiggle 0.5s ease-in-out;
}
</style>
